import React from 'react';
import { INSPECTION_STAGES } from '@skd/shared';

interface StepProgressProps {
  currentStep: number;
  onStepClick: (step: number) => void;
}

const STEPS = [
  { step: 0, label: 'FP', title: 'Front Page' },
  ...INSPECTION_STAGES.map((s) => ({ step: s.id, label: String(s.id), title: s.shortName })),
];

export function StepProgress({ currentStep, onStepClick }: StepProgressProps) {
  const current = STEPS.find((s) => s.step === currentStep) ?? STEPS[0];
  const index = STEPS.findIndex((s) => s.step === currentStep);
  const position = index >= 0 ? index + 1 : 1;

  return (
    <nav
      className="rounded-[var(--radius-lg)] border border-[var(--card-border)] bg-[var(--card-bg)] p-3 sm:p-4"
      aria-label="Inspection steps"
    >
      {/* Phone gets an explicit "where am I" line — the dots alone say nothing. */}
      <div className="mb-2.5 flex items-baseline justify-between gap-2 md:hidden">
        <span className="truncate text-[0.875rem] font-medium text-[var(--text-main)]">
          {current.title}
        </span>
        <span className="flex-shrink-0 text-[0.75rem] tnum text-[var(--text-dim)]">
          Step {position} of {STEPS.length}
        </span>
      </div>

      <ol className="flex items-center gap-1 overflow-x-auto md:gap-0">
        {STEPS.map((s, i) => {
          const isCurrent = currentStep === s.step;
          const isDone = currentStep > s.step;

          return (
            <li key={s.step} className="flex flex-1 items-center last:flex-none">
              <button
                type="button"
                onClick={() => onStepClick(s.step)}
                aria-current={isCurrent ? 'step' : undefined}
                title={s.title}
                className={`flex flex-shrink-0 items-center gap-1.5 rounded-[var(--radius)] px-1.5 py-1 text-[0.75rem] font-medium transition-colors md:px-2 ${
                  isCurrent
                    ? 'text-[var(--accent-color)]'
                    : 'text-[var(--text-dim)] hover:text-[var(--text-main)]'
                }`}
              >
                <span
                  className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-[0.6875rem] font-semibold transition-colors ${
                    isCurrent
                      ? 'bg-[var(--accent-color)] text-white'
                      : isDone
                      ? 'bg-[var(--accent-light)] text-[var(--accent-color)]'
                      : 'bg-[var(--card-bg-subtle)] text-[var(--text-dim)] ring-1 ring-inset ring-[var(--border-color)]'
                  }`}
                >
                  {isDone ? (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  ) : (
                    s.label
                  )}
                </span>
                <span className="hidden whitespace-nowrap lg:inline">{s.title}</span>
              </button>

              {i < STEPS.length - 1 && (
                <span
                  aria-hidden="true"
                  className={`mx-0.5 h-px min-w-[8px] flex-1 ${
                    isDone ? 'bg-[var(--accent-color)]' : 'bg-[var(--border-color)]'
                  }`}
                />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
